import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000",
  headers: {
    "Content-Type": "application/json",
  },
})

// Same shared-instance + interceptor pattern as bulkBadgeService, so every
// call automatically carries the auth token and X-Exhibition-ID header.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
      Object.assign(config.headers, getExhibitionHeader())
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ---- Types --------------------------------------------------------------

export interface ConsolidatedDashboardSummary {
  total_pending_badges: number
  not_yet_queued: number
  already_queued: number
  contributing_companies: number
  original_print_orders: number
  printable_sheets: number
  remaining_after_grouping: number
  active_consolidated_orders: number
}

export interface EligibleBadge {
  order_detail_id: number
  representation_id: number
  person_id: number
  person_full_name: string
  original_print_order_id: number
  original_order_number: string | null
}

export interface EligibleCompanyGroup {
  exhibitor_id: number
  exhibitor_name: string
  badges: EligibleBadge[]
}

export interface QueueBadge {
  item_id: number
  order_detail_id: number
  representation_id: number
  person_id: number
  person_full_name: string
  exhibition_title: string | null
  original_print_order_id: number
  original_order_number: string | null
  status: "pending" | "printed" | "removed"
}

export interface QueueCompanyGroup {
  exhibitor_id: number
  exhibitor_name: string
  badges: QueueBadge[]
}

export interface ConsolidatedQueue {
  consolidated_order_id: number
  order_number: string
  status: string
  force_print: boolean
  companies: QueueCompanyGroup[]
  total_pending: number
  full_sheets: number
  remainder: number
}

export interface ConsolidatedOrderSummary {
  id: number
  order_number: string
  status: string
  force_print: boolean
  completed: boolean
  notes: string | null
  created_by: number | null
  created_at: string
  printed_at: string | null
  pending_count: number
  printed_count: number
}

export interface ConsolidatedBadgeResult {
  item_id: number
  representation_id: number
  badge_data: {
    badge_info: {
      person_full_name: string
      role: string
      exhibitor_name: string
      exhibition_title: string
      stand_category: string
      verification_code: string
      role_color?: string
    }
    photo_url: string | null
    has_photo: boolean
  }
  success: true
}

export interface ConsolidatedGenerateResponse {
  success: boolean
  message: string
  data: {
    badge_results: ConsolidatedBadgeResult[]
    failed_badges: Array<{ item_id: number; representation_id: number; error: string; success: false }>
    sheets_printed: number | null
    forced: boolean
  }
}

class ConsolidatedPrintService {
  async getDashboard(): Promise<{ success: boolean; summary: ConsolidatedDashboardSummary }> {
    const response = await api.get("/api/badges/consolidated-print/dashboard/")
    return response.data
  }

  async getEligiblePreview(): Promise<{ success: boolean; companies: EligibleCompanyGroup[]; total_eligible: number }> {
    const response = await api.get("/api/badges/consolidated-print/eligible/")
    return response.data
  }

  async listOrders(statusFilter?: string): Promise<{ success: boolean; consolidated_orders: ConsolidatedOrderSummary[]; count: number }> {
    const response = await api.get("/api/badges/consolidated-print/orders/", {
      params: statusFilter ? { status: statusFilter } : undefined,
    })
    return response.data
  }

  async createOrder(orderDetailIds?: number[], notes?: string): Promise<{
    success: boolean
    message: string
    consolidated_order_id: number
    order_number: string
    queue: ConsolidatedQueue
  }> {
    try {
      const response = await api.post("/api/badges/consolidated-print/orders/create/", {
        order_detail_ids: orderDetailIds,
        notes,
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) throw error.response.data
      throw error
    }
  }

  async getOrder(consolidatedOrderId: number): Promise<{ success: boolean; queue: ConsolidatedQueue }> {
    const response = await api.get(`/api/badges/consolidated-print/orders/${consolidatedOrderId}/`)
    return response.data
  }

  async cancelOrder(consolidatedOrderId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/api/badges/consolidated-print/orders/${consolidatedOrderId}/cancel/`)
    return response.data
  }

  async removeItem(itemId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/api/badges/consolidated-print/items/${itemId}/`)
    return response.data
  }

  /** Generates only complete 4-badge sheets from the current queue. */
  async generateBatch(consolidatedOrderId: number): Promise<ConsolidatedGenerateResponse> {
    try {
      const response = await api.post(
        `/api/badges/consolidated-print/orders/${consolidatedOrderId}/generate/`,
        {},
        { timeout: 60000 },
      )
      return response.data
    } catch (error: any) {
      if (error.response?.data) throw error.response.data
      throw error
    }
  }

  /** Admin-only: opt this consolidated order into allowing a final partial sheet. */
  async enableForcePrint(consolidatedOrderId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/api/badges/consolidated-print/orders/${consolidatedOrderId}/enable-force-print/`)
      return response.data
    } catch (error: any) {
      if (error.response?.data) throw error.response.data
      throw error
    }
  }

  /** Prints every remaining pending badge, including a final partial sheet. Requires explicit confirm. */
  async forcePrintRemaining(consolidatedOrderId: number): Promise<ConsolidatedGenerateResponse> {
    try {
      const response = await api.post(
        `/api/badges/consolidated-print/orders/${consolidatedOrderId}/force-print/`,
        { confirm: true },
        { timeout: 60000 },
      )
      return response.data
    } catch (error: any) {
      if (error.response?.data) throw error.response.data
      throw error
    }
  }

  async completeOrder(consolidatedOrderId: number): Promise<{
    success: boolean
    message: string
    consolidated_order_id: number
    status: string
    original_print_orders_updated: number[]
  }> {
    try {
      const response = await api.post(`/api/badges/consolidated-print/orders/${consolidatedOrderId}/complete/`)
      return response.data
    } catch (error: any) {
      if (error.response?.data) throw error.response.data
      throw error
    }
  }
}

export const consolidatedPrintService = new ConsolidatedPrintService()
